import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Form, Input, Alert, Checkbox, Modal, Radio, message } from 'antd';
import { ArrowRightIcon, BeakerIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { login, isAuthenticated } from '../services/authService';
import { startDemoSession } from '../demo/demoMode';
import { resetDemoStore } from '../demo/demoApi';
import LOGIN_PHOTOS from '../config/loginPhotos.json';

// KR_FORA 셀러 로그인과 같은 구성: 왼쪽 여행 사진(KR_FORA 도시 사진 목록, iwinv 원본 URL) + 오른쪽 로그인 카드.
// 사진은 들어올 때마다 새로 고른다.
const pickPhoto = () => LOGIN_PHOTOS[Math.floor(Math.random() * LOGIN_PHOTOS.length)];

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [custModalOpen, setCustModalOpen] = useState(false);
  const [custList, setCustList] = useState([]);
  const [selectedCustNo, setSelectedCustNo] = useState('');
  const [pendingLogin, setPendingLogin] = useState(null);
  const [pwResetModalOpen, setPwResetModalOpen] = useState(false);
  const [pwResetEmail, setPwResetEmail] = useState('');
  const [pwResetLoading, setPwResetLoading] = useState(false);
  const [pwResetError, setPwResetError] = useState('');
  const [photoUrl] = useState(pickPhoto);
  const navigate = useNavigate();
  const location = useLocation();

  // If already logged in, redirect
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/products/tour', { replace: true });
    }
  }, [navigate]);

  const onFinish = async (values) => {
    setError('');
    setLoading(true);
    try {
      await login(values.userName, values.password, !!values.remember);
      const from = location.state?.from?.pathname || '/products/tour';
      navigate(from, { replace: true });
    } catch (e) {
      if (e?.code === 'MULTI_CUST' && Array.isArray(e?.custList) && e.custList.length > 0) {
        setPendingLogin({
          userName: values.userName,
          password: values.password,
          remember: !!values.remember,
          from: location.state?.from?.pathname || '/products/tour',
        });
        setCustList(e.custList);
        setSelectedCustNo(String(e.custList[0]?.cust_no || ''));
        setCustModalOpen(true);
        setError('');
        return;
      }
      setError(e?.message || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openPwResetModal = () => {
    setPwResetEmail('');
    setPwResetError('');
    setPwResetModalOpen(true);
  };

  const handlePwReset = async () => {
    const email = pwResetEmail.trim();
    if (!email) {
      setPwResetError('아이디(이메일)을 입력해주세요.');
      return;
    }

    setPwResetError('');
    setPwResetLoading(true);
    try {
      const url = `https://trpliveapi1.tripbox.co.kr/Users/PasswordResetEmailSend?userID=${encodeURIComponent(email)}`;
      const resp = await fetch(url, {
        method: 'GET',
      });

      const contentType = resp.headers.get('content-type') || '';
      let body;
      if (contentType.includes('application/json')) {
        body = await resp.json();
      } else {
        body = await resp.text();
      }

      if (!resp.ok) {
        throw new Error('비밀번호 초기화 요청에 실패했습니다.');
      }

      if (typeof body === 'string') {
        if (body.trim() === 'success') {
          message.success('임시 비밀번호가 이메일로 발송되었습니다. 이메일을 확인해주세요.');
          setPwResetModalOpen(false);
          setPwResetEmail('');
          return;
        }
        // 문자열이지만 success 가 아닌 경우 일반 오류 처리
        setPwResetError('비밀번호 초기화에 실패했습니다. 다시 시도해주세요.');
        return;
      }

      if (body && typeof body === 'object' && typeof body.message === 'string') {
        if (body.message === '회원 정보가 잘못 되었습니다') {
          setPwResetError('회원 정보가 잘못 되었습니다');
          return;
        }
        setPwResetError(body.message);
        return;
      }

      setPwResetError('비밀번호 초기화에 실패했습니다. 다시 시도해주세요.');
    } catch (e) {
      setPwResetError(e?.message || '비밀번호 초기화에 실패했습니다.');
    } finally {
      setPwResetLoading(false);
    }
  };

  const onConfirmCust = async () => {
    if (!pendingLogin) return;
    if (!selectedCustNo) {
      setError('로그인할 회사를 선택해주세요.');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await login(pendingLogin.userName, pendingLogin.password, pendingLogin.remember, selectedCustNo);
      setCustModalOpen(false);
      setCustList([]);
      setSelectedCustNo('');
      setPendingLogin(null);
      navigate(pendingLogin.from, { replace: true });
    } catch (e) {
      setError(e?.message || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 데모 버전: 실제 서버에 붙지 않고 샘플 데이터로만 동작하는 체험 모드로 들어간다.
  const onStartDemo = () => {
    resetDemoStore();
    startDemoSession();
    navigate('/products/tour', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      {/* 왼쪽: 여행 사진 */}
      <aside className="relative h-[38vh] shrink-0 overflow-hidden bg-gray-900 md:h-auto md:min-h-screen md:w-1/2">
        <img src={photoUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/15" />
        <p
          className="absolute left-6 top-6 text-[28px] tracking-[0.04em] text-white md:left-10 md:top-8 md:text-[34px]"
          style={{ fontFamily: "'Libre Baskerville', Georgia, serif" }}
        >
          tripbox
        </p>
      </aside>

      {/* 오른쪽: 로그인 */}
      <main className="flex flex-1 flex-col bg-white md:min-h-screen md:w-1/2">
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 md:px-10">
          <div className="flex w-full max-w-[400px] flex-col gap-8 rounded-sm border border-gray-200 px-8 py-10">
            <div className="flex flex-col items-center text-center">
              <p className="text-[15px] text-gray-500">환영합니다</p>
              <h1 className="mt-1 text-[22px] font-bold tracking-tight text-gray-900">여행사 업무 시스템</h1>
            </div>

            <div className="flex w-full flex-col gap-5">
              {error && <Alert message={error} type="error" showIcon />}

              <Form
                layout="vertical"
                requiredMark={false}
                onFinish={onFinish}
                initialValues={{ userName: '', password: '', remember: true }}
                className="flex flex-col"
              >
                <Form.Item
                  label={<span className="text-[13px] font-semibold text-gray-600">이메일</span>}
                  name="userName"
                  rules={[{ required: true, message: '아이디(이메일)를 입력해주세요.' }]}
                  style={{ marginBottom: 12 }}
                >
                  <Input size="large" autoComplete="username" placeholder="이메일을 입력하세요" />
                </Form.Item>

                <Form.Item
                  label={<span className="text-[13px] font-semibold text-gray-600">비밀번호</span>}
                  name="password"
                  rules={[{ required: true, message: '비밀번호를 입력해주세요.' }]}
                  style={{ marginBottom: 12 }}
                >
                  <Input.Password size="large" autoComplete="current-password" placeholder="비밀번호를 입력하세요" />
                </Form.Item>

                <div className="flex items-center justify-between">
                  <Form.Item name="remember" valuePropName="checked" noStyle>
                    <Checkbox className="text-gray-500">로그인 유지</Checkbox>
                  </Form.Item>
                  <button
                    type="button"
                    onClick={openPwResetModal}
                    className="text-[13px] font-medium text-gray-500 hover:text-gray-900"
                  >
                    비밀번호 찾기
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-4 flex h-11 w-full items-center justify-center gap-1.5 rounded-lg bg-gray-900 text-[15px] font-semibold text-white transition-colors hover:bg-gray-800 disabled:opacity-60"
                >
                  {loading && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                  )}
                  이메일로 로그인
                </button>
              </Form>

              {/* 데모 버전 진입 (마케팅 · 튜토리얼용) */}
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={onStartDemo}
                  className="flex h-11 w-full items-center justify-center gap-1.5 rounded-lg bg-gray-100 text-[15px] font-semibold text-gray-900 transition-colors hover:bg-gray-200"
                >
                  <BeakerIcon className="h-4 w-4" />
                  데모 버전 체험하기
                </button>
                <p className="text-center text-xs text-gray-500">
                  가입 없이 샘플 데이터로 둘러볼 수 있습니다. 화면마다 안내 설명이 함께 나옵니다.
                </p>
              </div>

              <p className="text-center text-[13px] text-gray-500">회원가입 문의 : 02-6080-8800</p>
            </div>
          </div>

          {/* 로그인하지 않아도 볼 수 있는 사용 안내 */}
          <button
            type="button"
            onClick={() => navigate('/manuals')}
            className="mt-4 inline-flex w-full max-w-[400px] items-center justify-center gap-1.5 rounded-sm border border-gray-200 px-4 py-3 text-[13px] font-semibold text-gray-900 hover:bg-gray-50"
          >
            <BookOpenIcon className="h-4 w-4" />
            매뉴얼 보기
            <ArrowRightIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        <footer className="px-6 pb-6 text-center text-[11px] leading-relaxed text-gray-500">
          © {new Date().getFullYear()} tripbox. All Rights Reserved.
        </footer>

        <Modal
          title="회사 선택"
          open={custModalOpen}
          onOk={onConfirmCust}
          okText="선택"
          cancelText="취소"
          confirmLoading={loading}
          onCancel={() => {
            setCustModalOpen(false);
            setCustList([]);
            setSelectedCustNo('');
            setPendingLogin(null);
          }}
        >
          <Radio.Group
            value={selectedCustNo}
            onChange={(e) => setSelectedCustNo(String(e.target.value))}
            style={{ width: '100%' }}
          >
            {custList.map((c) => (
              <div key={String(c.cust_no)} style={{ padding: '6px 0' }}>
                <Radio value={String(c.cust_no)}>
                  {c.cust_name} ({c.cust_no})
                </Radio>
              </div>
            ))}
          </Radio.Group>
        </Modal>

        <Modal
          title="비밀번호 초기화"
          open={pwResetModalOpen}
          onCancel={() => {
            if (pwResetLoading) return;
            setPwResetModalOpen(false);
            setPwResetEmail('');
            setPwResetError('');
          }}
          footer={null}
        >
          <div style={{ marginBottom: 16, whiteSpace: 'pre-line' }}>
            비밀번호를 잊으셨나요?

            회원 가입하신 이메일 주소를 입력해 주시면

            임시 비밀번호를 이메일로 발급해 드립니다.

            로그인 후 마이페이지에서 비밀번호를 변경 하세요.
          </div>

          <Form layout="vertical" onFinish={handlePwReset}>
            <Form.Item label="아이디 (이메일)">
              <Input
                value={pwResetEmail}
                onChange={(e) => {
                  setPwResetEmail(e.target.value);
                  if (pwResetError) setPwResetError('');
                }}
                placeholder="이메일을 입력하세요"
              />
            </Form.Item>

            {pwResetError && (
              <div style={{ color: '#ff4d4f', marginBottom: 16 }}>{pwResetError}</div>
            )}

            <div style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={pwResetLoading}
              >
                임시 비밀번호 발급
              </Button>
            </div>
          </Form>
        </Modal>
      </main>
    </div>
  );
};

export default Login;
